import React from 'react'

import Wizard from '@modules/form-wizard'
import SERVICE_PROPS from '@helpers/extract-service-props'
import { OPENMAPFIELDS } from '@modules/open-street-map-field'
import moment from 'moment'
import Swal from 'sweetalert2'
import toastr from '@modules/toastr'
import { fullName } from '@modules/form-wizard/utils'

export default class GarbageCollectionComplaint extends React.Component {
  resource = '/moen/health/garbage_collection_complaints'
  form_key = 'garbage_collection_complaint'
  defaults = {}

  // Delete lines as needed
  fee = 99
  free = true
  postpaid = true
  skip_location = true
  hide_buttons = false
  custom_actions = [
    {
      text: 'Assign Officer',
      icon: 'bullhorn',
      roles: [['senior_officer']],
      test: r => ['pending', 'processing'].includes(r.application_decision),
      fn: async record => {
        // Get the list of officers
        const officerList = {};
        const { data } = await $app.axios.get(`${this.resource}/show_field_officers`)
        const officerMap = data.records.map(x => {
          return { id: x.id, name: `${x.first_name} ${x.last_name}` }
        })
        this.setState({ all_reviewers: officerMap })
        this.state.all_reviewers.forEach(e => {
          officerList[`${e.id}`] = e.name;
        })

        let officerID = null
        const {isDismissed, value} = await Swal.fire({
          icon: 'question',
          title: 'Assign Officer for inspection',
          html: `
            Select which field officer you would wish to assign to this application.
          `,
          input: 'select',
          inputOptions: officerList,
          showCancelButton: true,
          confirmButtonText: 'Send',
        })
  
        if (isDismissed) return
        officerID = value
        // console.log(officerID)

        const { props, resource, form_key } = this
        const { history, location } = props

        try {
          await $app.axios.put(resource + '/update_application', {
            [form_key]: {
              form_num: record.form_num,
              application_decision: 'assign reviewer',
              reviewer_id: officerID,
            },
          })

          if (location?.state?.view) {
            const view = { ...location.state.view }
            console.log(view)
            view.application_decision = 'assigned reviewer'

            history.replace(location.pathname, {
              ...location.state,
              view,
            })
          }

          toastr.success('Success', 'Application approved for review successfully')
        } catch (error) {
          console.error(error)
          Swal.fire(
            'Oops...',
            error.response?.data?.error || 'An error occurred while approving the application for review',
            'error'
          )
        }
      }
    },
    {
      text: 'Claim Application',
      icon: 'check',
      roles: [['senior_officer']],
      test: r => {
        if (['pending', 'processing'].includes(r.application_decision) && r.reviewer_id == null) {
          return true
        }

        if (['pending', 'processing'].includes(r.application_decision) && $app.current_user.roles.includes('senior_officer') && r.reviewer_id != $app.current_user.id) {
          return true
        }

        return false
      },
      fn: async record => {
        const { isDismissed, value } = await Swal.fire({
          icon: 'question',
          title: 'Claim Application for Review',
          html: `
            Are you claiming this application for review, 
            <strong>${fullName(record.user, 'initial')}</strong>?
          `,
          input: 'select',
          inputOptions: {
            'claimed': "I claim this application for review",
            'no': "I do not wish to claim this application",            
          },
          showCancelButton: true,
        })

        if (isDismissed) return
        if (value === 'no') return

        await this.submitToNewLevel(value, record, null)
      }
    },
    {
      text: 'Confirm Inspection Date',
      icon: 'check-square',
      roles: [['field_officer', 'inspector', 'senior_officer']],
      test: r => {
        if (['pending', 'processing', 'document requested'].includes(r.application_decision) && r.reviewer_id == $app.current_user.id) {
          return true
        }

        return false
      },
      fn: async record => {
        let inspectionDate

        // Set value of Inspection Date
        const datePrompt = await Swal.fire({
          icon: 'question',
          title: 'Inspection Date?',
          html: `
            <p>
              What is your recommended inspection date regarding the applicant,
              <strong>${fullName(record.user, 'initial')}</strong>,
              <br/>
              <span><b>Organization Name:</b> <u>${record.organization_name || 'N/A'}</u></span>,
              <br/>
              <span><b>Vendor Type:</b> <u>${record.vendor_type}</u></span>
              <br/>
              <span><b>Address:</b> <u>${record.address}</u></span>
            </p>

            <div>
              <input type="date" id="inspection-date" class="swal2-input" min="${moment().format('YYYY-MM-DD')}">
            </div>
          `,

          showCancelButton: true,
          confirmButtonText: 'Submit',
          preConfirm: () => {
            const date = (document.getElementById('inspection-date')).value;
            if (!date) {
              Swal.showValidationMessage('Please select a date');
            }
            console.log(date)
            return date;
          },
        })
  
        if (datePrompt.isDismissed) return

        inspectionDate = datePrompt.value
    
        if (inspectionDate) {
          const { isConfirmed } = await Swal.fire({
            icon: 'question',
            title: 'Confirm Details',
            html: `
              <p>
                What is your recommended inspection date regarding the applicant,
                <strong>${fullName(record.user, 'initial')}</strong>,
                <br/>
                <span><b>Organization Name:</b> <u>${record.organization_name || 'N/A'}</u></span>,
                <br/>
                <span><b>Vendor Type:</b> <u>${record.vendor_type}</u></span>
                <br/>
                <span><b>Address:</b> <u>${record.address}</u></span>
              </p>
              Inspection date to be: <b>${inspectionDate}</b>
              <br/>
            `,
            showCancelButton: true,
            confirmButtonText: 'Submit',
          })
    
          if (isConfirmed) {
            await this.submitToNewLevel('assigned date', record, inspectionDate)
          }
        }
      }
    },
    {
      text: 'Approve',
      icon: 'check',
      roles: [['field_officer', 'inspector', 'senior_officer']],
      test: r => {
        if (['pending', 'processing', 'document requested'].includes(r.application_decision) && r.reviewer_id == $app.current_user.id) {
          return true
        }

        return false
      },
      fn: record =>
        Swal.fire({
          icon: 'success',
          title: 'Confirm Approval',
          text: 'Are you sure you want to approve this application?',
          confirmButtonText: 'Approve',
          showCancelButton: true,
        }).then(async result => {
          if (result.isConfirmed) {
            // await this.approve(record)
          }
        }),
    },
    {
      text: 'Deny',
      icon: 'ban',
      roles: [['field_officer', 'inspector', 'senior_officer']],
      test: r => {
        if (['pending', 'processing', 'document requested'].includes(r.application_decision) && r.reviewer_id == $app.current_user.id) {
          return true
        }

        return false
      },
      fn: record =>
        Swal.fire({
          icon: 'error',
          title: 'Confirm Denial',
          text: 'Are you sure you want to deny this application?',
          confirmButtonText: 'Deny',
          showCancelButton: true,
        }).then(async result => {
          if (result.isConfirmed) {
            // await this.deny(record)
          }
        }),
    },
  ]

  submitToNewLevel = async (value, record, inspectionDate = null) => {
    this.setState({ loading: true })
    const { resource, form_key } = this
    const { axios } = $app

    try {
      await axios.put(resource + '/update_application', {
        [form_key]: {
          form_num: record.form_num,
          application_decision: value,
          inspection_date: inspectionDate,
        },
      })

      toastr.success('Success', 'The application was successfully updated')
    } catch (error) {
      console.error(error)
      toastr.error('Error', error.response?.data?.error || 'Something went wrong with trying to update this application')
    } finally {
      this.setState({ loading: false })
    }
  }

  columns = props => [
    {
      name: 'Inspection Date',
      selector: row => row.inspection_date ? moment(row.inspection_date).format('MMMM Do YYYY, h:mm a') : 'N/A',
      sortable: true,
      searchable: r => r.inspection_date,
    },
    {
      name: 'Officer Assigned',
      width: '200px',
      selector: row => row.reviewer ? `
        ${row.reviewer.first_name[0].toUpperCase() + row.reviewer.first_name.substring(1)} ${row.reviewer.last_name[0].toUpperCase() + row.reviewer.last_name.substring(1)}
      ` : 'N/A',
      sortable: true,
      // searchable: r => r.reviewer,
    },
  ]

  validate = async form => {
    const update = {}
    const complaintsArray = Array.from(form.complaint_type || []) // Convert Checklist of type Set to Array
    update.complaint_type = complaintsArray

    // Validation: Ensure at least one complaint type is selected
    if (complaintsArray.length === 0) {
      return 'No Complaint Types were selected'
    }

    if (form.property_type.trim() === "") {
      return 'No Property Type was selected'
    }

    return update
  }

  foremost = null

  fields = (form, formRef) => [
    {
      name: 'inspection_date',
      type: 'date',
      showYearDropdown: true,
      hide: true,
      view: {
        label: 'Inspection Date',
        value: v => (v ? moment(v).format('MMMM Do YYYY, h:mm a') : 'N/A')
      },
    },
  
    "::Contact Information",
    {
      name: "contact_number",
      label: "Contact Phone #",
      type: "phone"
    },

    "::Complaint Details",
    {
      name: "building_number",
      label: "Building Number",
      type: "integer",
      required: false,
    },
    {
      name: "property_type",
      label: "Property Type",
      type: "checklist",
      single: true,
      options: [
        "House",
        "Apartment",
        "Commercial",
      ],
    },
    {
      name: "complaint_type",
      label: "Nature of Complaint",
      type: "checklist",
      multi: true,
      options: [
        { label: "Non-Collection of Garbage", value: "non-collection of garbage" },
        { label: "Displaced Bins", value: "displaced bins" },
        { label: "Derelict Vehicle", value: "derelict vehicle" },
      ],
      hint: "* Select all that apply",
    },
    {
      name: "complaint_desc",
      label: "Give Details",
      type: "textarea",
      hint: `Please provide any additional details that may assist in the investigation of your complaint
      `,
    },

    "::Location of Complaint",
    ...OPENMAPFIELDS(this, form, formRef, null),
    {
      name: "constituency"
    },
    {
      name: "street_address",
      hint: "eg. Collins Avenue, #5"
    },
    {
      name: "side",
      label: "Which side of the building?",
      options: ["Left Side", "Right Side"]
    },
    {
      name: "building_desc",
      label: "Color & Description of the Building",
      type: "textarea"
    }
  ]

  render = () => <Wizard {...SERVICE_PROPS(this)} />
}
